import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageVisibility } from "@/lib/usePageVisibility";
import ComingSoon from "@/components/ComingSoon";

/**
 * Wraps a public page route. Behaviour driven by the admin in /admin/page-visibility:
 *   - status="live"        → renders children
 *   - status="coming_soon" → renders the ComingSoon placeholder
 *   - status="hidden"      → navigates to the not-found view (effectively 404)
 *
 * If the page isn't in the catalog at all, children render as-is (fail open).
 */
export default function PageGuard({ pageKey, children }) {
  const { byKey, loading } = usePageVisibility();
  const navigate = useNavigate();
  const entry = byKey[pageKey];
  const status = entry?.status;

  useEffect(() => {
    if (!loading && status === "hidden") {
      // Replace history so back button doesn't bounce them right back here.
      navigate("/not-found", { replace: true });
    }
  }, [loading, status, navigate]);

  if (loading) {
    // Don't paint the page until we know — avoids a flash of real content
    // before the placeholder kicks in.
    return (
      <div className="min-h-screen bg-[#050505]" data-testid={`page-guard-loading-${pageKey}`} />
    );
  }

  if (status === "hidden") {
    return null;
  }

  if (status === "coming_soon" && entry) {
    return (
      <ComingSoon
        pageKey={pageKey}
        title={entry.coming_soon_title}
        body={entry.coming_soon_body}
        notifyEnabled={entry.notify_signup_enabled !== false}
      />
    );
  }

  return children;
}
